import {
  useCallback,
  useEffect,
  useMemo,
  useState } from "react";
import { getAllPrenotazioni } from "../APIs/ReservationsAPI";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Typography
} from "@mui/material";
import TextField from '../components/SearchableTextField';
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import { useTranslation } from "react-i18next";
import { SetupWSEventListener } from "../utils/setupWSEventListener";
import { getTenantTranslation } from "../utils/getTenantTranslation";
import DateTimeLocal24Field from "../components/DateTimeLocal24Field";
import { localizedErrorMessage } from "../utils/italianErrorMessage";

const COMPANY_PLACEHOLDER_SURNAME = "[AZIENDA]";
const RESERVATION_STATUSES = ["ALL", "PREVENTIVO", "TO_CONFIRM", "CONFIRMED", "COMPLETED", "CANCELLED", "NO_SHOW"];

const formatDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("it-IT", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const toDateFromInput = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const buildSearchText = (reservation, tableSummary, packageSummary, servicesSummary, recipientLabel) => {
  return [
    reservation?.eventName,
    recipientLabel,
    reservation?.client?.email,
    reservation?.client?.phone,
    reservation?.client?.company?.companyName,
    tableSummary,
    packageSummary,
    servicesSummary,
    reservation?.note,
    reservation?.cancellationReason,
    reservation?.status,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

function StampaPrenotazioni() {
  const { t } = useTranslation();
  const tenantType = localStorage.getItem("tenantType");

  const [reservations, setReservations] = useState([]);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");

  const getStatusLabel = useCallback((status) => {
    if (!status) return "-";
    return t(`reservations_details.status_values.${status}`, { defaultValue: status });
  }, [t]);

  const getStatusChipSx = useCallback((status) => {
    switch (status) {
      case "PREVENTIVO":
        return { backgroundColor: "#FFF3E0", color: "#9A4700", fontWeight: 700 };
      case "TO_CONFIRM":
        return { backgroundColor: "#EDE7F6", color: "#4A148C", fontWeight: 700 };
      case "CONFIRMED":
        return { backgroundColor: "#E3F2FD", color: "#0D47A1", fontWeight: 700 };
      case "COMPLETED":
        return { backgroundColor: "#E8F5E9", color: "#1B5E20", fontWeight: 700 };
      case "CANCELLED":
        return { backgroundColor: "#FFEBEE", color: "#B71C1C", fontWeight: 700 };
      case "NO_SHOW":
        return { backgroundColor: "#FFF8E1", color: "#E65100", fontWeight: 700 };
      default:
        return { backgroundColor: "#ECEFF1", color: "#263238", fontWeight: 700 };
    }
  }, []);

  const getRecipientLabel = useCallback((reservation) => {
    const client = reservation?.client;
    const companyName = client?.company?.companyName || "";
    const name = (client?.name || "").trim();
    const surname = (client?.surname || "").trim();

    if (client && surname === COMPANY_PLACEHOLDER_SURNAME) {
      return companyName || name || "-";
    }

    const fullName = `${name} ${surname}`.trim();
    if (client) return fullName || companyName || "-";

    const heliteName = (reservation?.heliteHolderName || "").trim();
    const heliteRoom = (reservation?.heliteRoomNumber || "").trim();
    if (heliteName || heliteRoom) {
      return [heliteName, heliteRoom ? `Camera ${heliteRoom}` : null].filter(Boolean).join(" - ");
    }

    return reservation?.eventName?.trim() || "-";
  }, []);

  const formatTableSummary = useCallback((tableReservations = []) => {
    if (!Array.isArray(tableReservations) || tableReservations.length === 0) return "-";

    const roomLabel = getTenantTranslation(t, tenantType, "reservationRoom") || t("room");
    const tableLabel = getTenantTranslation(t, tenantType, "bookingTable") || t("table");

    const grouped = new Map();
    const fallback = [];

    tableReservations.forEach((tr) => {
      const tableName = tr?.table?.name || "";
      const cleanTableName = /^\d+ \d+$/.test(tableName) ? tableName.replace(/^\d+ /, "") : tableName;
      const roomName = tr?.table?.room?.name || "";
      const renderedTable = cleanTableName ? `${tableLabel} ${cleanTableName}` : tableLabel;

      if (!roomName) {
        fallback.push(renderedTable);
        return;
      }

      if (!grouped.has(roomName)) grouped.set(roomName, []);
      grouped.get(roomName).push(renderedTable);
    });

    const groupedLabels = Array.from(grouped.entries()).map(([roomName, tableLabels]) => (
      `${roomLabel} ${roomName}: ${tableLabels.join(", ")}`
    ));

    return [...groupedLabels, ...fallback].join(" | ");
  }, [t, tenantType]);

  const formatPackages = useCallback((reservation) => {
    const entries = Array.isArray(reservation?.reservationPackages) ? reservation.reservationPackages : [];
    if (!entries.length) return "-";

    return entries
      .map((entry) => {
        const pkg = entry?.package;
        if (!pkg) return null;
        const priceLabel = Number.isFinite(Number(pkg?.price)) ? ` (${Number(pkg.price).toFixed(2)} EUR)` : "";
        return `${pkg?.name || `#${pkg?.id}`}${priceLabel}`;
      })
      .filter(Boolean)
      .join(", ") || "-";
  }, []);

  const formatServices = useCallback((reservation) => {
    const services = Array.isArray(reservation?.eventDay?.services) ? reservation.eventDay.services : [];
    if (!services.length) return "-";

    return services
      .map((entry) => {
        const productName = entry?.product?.name || `#${entry?.productId ?? "-"}`;
        const variantName = entry?.variant?.name ? ` (${entry.variant.name})` : "";
        const quantity = Number(entry?.quantity || 1);
        return `${productName}${variantName} x${quantity}`;
      })
      .join(", ");
  }, []);

  const fetchReservations = useCallback(async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const tenant = localStorage.getItem("tenantId");
      const data = await getAllPrenotazioni(token, tenant, { noCache: true });
      setReservations(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(localizedErrorMessage(err, t("reservations_details.error")));
    }
  }, [t]);

  useEffect(() => {
    fetchReservations();
    const cleanup = SetupWSEventListener(fetchReservations);
    return () => cleanup();
  }, [fetchReservations]);

  const filteredReservations = useMemo(() => {
    const fromDate = toDateFromInput(rangeFrom);
    const toDate = toDateFromInput(rangeTo);

    return [...reservations]
      .filter((reservation) => {
        const start = new Date(reservation?.startDate);
        const end = new Date(reservation?.endDate);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;

        if (statusFilter !== "ALL" && (reservation?.status || "TO_CONFIRM") !== statusFilter) {
          return false;
        }

        if (fromDate && end < fromDate) return false;
        if (toDate && start > toDate) return false;

        if (!search.trim()) return true;

        const recipientLabel = getRecipientLabel(reservation);
        const tableSummary = formatTableSummary(reservation?.tableReservations);
        const packageSummary = formatPackages(reservation);
        const servicesSummary = formatServices(reservation);
        const haystack = buildSearchText(
          reservation,
          tableSummary,
          packageSummary,
          servicesSummary,
          recipientLabel
        );

        return haystack.includes(search.trim().toLowerCase());
      })
      .sort((a, b) => {
        const aTime = new Date(a?.startDate).getTime();
        const bTime = new Date(b?.startDate).getTime();
        return aTime - bTime;
      });
  }, [
    reservations,
    rangeFrom,
    rangeTo,
    statusFilter,
    search,
    getRecipientLabel,
    formatTableSummary,
    formatPackages,
    formatServices,
  ]);

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("ALL");
    setRangeFrom("");
    setRangeTo("");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f3f4f6",
        p: { xs: 2, md: 3 },
        "@media print": {
          backgroundColor: "#ffffff",
          p: 1.5,
        },
      }}
    >
      <Paper
        className="no-print"
        elevation={0}
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          mb: 2,
          "@media print": {
            display: "none",
          },
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              {getTenantTranslation(t, tenantType, "reservations_details.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("reservations_details.print_subtitle", { defaultValue: "Report completo prenotazioni con filtri e stampa" })}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<RefreshOutlinedIcon />} onClick={fetchReservations}>
              {t("refresh", { defaultValue: "Aggiorna" })}
            </Button>
            <Button variant="contained" startIcon={<PrintOutlinedIcon />} onClick={() => window.print()}>
              {t("print")}
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={1.5}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={t("search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label={t("reservations_details.status")}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              size="small"
            >
              <MenuItem value="ALL">{t("all")}</MenuItem>
              {RESERVATION_STATUSES.filter((item) => item !== "ALL").map((status) => (
                <MenuItem key={status} value={status}>
                  {getStatusLabel(status)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <DateTimeLocal24Field
              label={t("from", { defaultValue: "Da" })}
              value={rangeFrom}
              onChange={setRangeFrom}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <DateTimeLocal24Field
              label={t("to", { defaultValue: "A" })}
              value={rangeTo}
              onChange={setRangeTo}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="text"
              color="inherit"
              startIcon={<RestartAltOutlinedIcon />}
              onClick={resetFilters}
              sx={{ minHeight: 40 }}
            >
              {t("clear")}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
          px: 0.5,
          "@media print": {
            mb: 1,
          },
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {t("reservations_details.filtered_count", {
            defaultValue: "{{count}} prenotazioni",
            count: filteredReservations.length,
          })}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t("generatedAt", { defaultValue: "Generato il" })} {formatDateTime(new Date())}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getTenantTranslation(t, tenantType, "reservations_details.error")}: {error}
        </Alert>
      )}

      {!error && filteredReservations.length === 0 && (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography>{getTenantTranslation(t, tenantType, "reservations_details.none")}</Typography>
        </Paper>
      )}

      <Grid container spacing={2}>
        {filteredReservations.map((reservation) => {
          const recipientLabel = getRecipientLabel(reservation);
          const tableSummary = formatTableSummary(reservation?.tableReservations);
          const packageSummary = formatPackages(reservation);
          const servicesSummary = formatServices(reservation);

          return (
            <Grid item xs={12} md={6} key={reservation.id} sx={{ "@media print": { pageBreakInside: "avoid" } }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.25,
                    backgroundColor: "#fafafa",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      #{reservation.id}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                      {reservation?.eventName?.trim() || t("bookingExtra.event", { defaultValue: "Evento" })}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={getStatusLabel(reservation?.status || "TO_CONFIRM")}
                    sx={getStatusChipSx(reservation?.status || "TO_CONFIRM")}
                  />
                </Box>

                <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {recipientLabel}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    {reservation?.client?.company?.companyName
                      || reservation?.heliteHolderEmail
                      || (reservation?.heliteBillNumber ? `Pren. HElite ${reservation.heliteBillNumber}` : "-")}
                    {reservation?.client?.email ? ` | ${reservation.client.email}` : ""}
                    {reservation?.client?.phone ? ` | ${reservation.client.phone}` : ""}
                  </Typography>

                  <Divider sx={{ my: 0.5 }} />

                  <Typography variant="body2">
                    <strong>{getTenantTranslation(t, tenantType, "bookingDate")}:</strong>{" "}
                    {formatDateTime(reservation?.startDate)} - {formatDateTime(reservation?.endDate)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{getTenantTranslation(t, tenantType, "bookingGuests")}:</strong>{" "}
                    {reservation?.guests ?? "-"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{getTenantTranslation(t, tenantType, "bookingTable")}:</strong> {tableSummary}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t("booking.packagesLabel")}:</strong> {packageSummary}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t("preventivi.available_services", { defaultValue: "Servizi" })}:</strong> {servicesSummary}
                  </Typography>

                  {reservation?.note && (
                    <Typography variant="body2">
                      <strong>{t("booking.notes")}:</strong> {reservation.note}
                    </Typography>
                  )}

                  {reservation?.status === "CANCELLED" && reservation?.cancellationReason && (
                    <Typography variant="body2" color="error.main">
                      <strong>{t("reservations_details.cancellation_reason")}:</strong> {reservation.cancellationReason}
                    </Typography>
                  )}

                  <Typography variant="caption" color="text.secondary">
                    {getTenantTranslation(t, tenantType, "reservations_details.createdAt")}: {formatDateTime(reservation?.createdAt)}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

export default StampaPrenotazioni;
